import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'

interface UseInputValidationOptions {
  min?: number
  max?: number
  required?: boolean
}

function isEmpty(v: unknown): boolean {
  if (v === undefined || v === null) return true
  if (typeof v === 'string') return v === ''
  if (typeof v === 'number') return Number.isNaN(v)
  return false
}

/**
 * Reusable validation composable for inputs.
 * `hasBlurred` is set on the first blur with a non-empty value, so tabbing
 * through an untouched field does not trigger errors.
 */
export function useInputValidation(
  value: MaybeRefOrGetter<unknown>,
  options: MaybeRefOrGetter<UseInputValidationOptions> = {},
) {
  const hasBlurred = ref(false)

  const isValid = computed(() => {
    const v = toValue(value)
    const { min, max, required } = toValue(options)
    if (required && isEmpty(v)) return false
    if (typeof v === 'number') {
      if (min != null && v < min) return false
      if (max != null && v > max) return false
    }
    return true
  })

  const showError = computed(() => hasBlurred.value && !isValid.value)

  function onBlur() {
    if (!isEmpty(toValue(value))) hasBlurred.value = true
  }

  // Force the error to surface — used on submit attempt so empty-required
  // and out-of-range values show their message even without a prior blur.
  function markBlurred() {
    hasBlurred.value = true
  }

  return {
    isValid,
    showError,
    onBlur,
    markBlurred,
  }
}
